
export interface ColorInfo {
    RAL: string; // RAL-koodi tai anodisoitu koodi
    name: string;
    HEX: string;
}

/**
 * Luokka, jonka avulla voi hakea RAL-koodin ja nimen,
 * kun syötteenä on joko RAL-koodi (merkkijono) tai HEX-väri.
 */
export class ColorConverter {
    /**
     * Kaikki RAL-värit: avaimena "1000", "1001", jne.
     * Arvona objekti { name, HEX }.
     * Voit halutessasi karsia listaa, jos tämä on liian laaja.
     */
    private static readonly RAL_DATA: Record<string, { name: string; HEX: string }> = {
        "1000": { name: "Green Beige", HEX: "#BEBD7F" },
        "1001": { name: "Beige", HEX: "#C2B078" },
        "1002": { name: "Sand Yellow", HEX: "#C6A664" },
        "1003": { name: "Signal Yellow", HEX: "#E5BE01" },
        "1004": { name: "Golden Yellow", HEX: "#CDA434" },
        "1005": { name: "Honey Yellow", HEX: "#A98307" },
        "1006": { name: "Maize Yellow", HEX: "#E4A010" },
        "1007": { name: "Daffodil Yellow", HEX: "#DC9D00" },
        "1011": { name: "Brown Beige", HEX: "#8A6642" },
        "1012": { name: "Lemon Yellow", HEX: "#C7B446" },
        "1013": { name: "Oyster White", HEX: "#EAE6CA" },
        "1014": { name: "Ivory", HEX: "#E1CC4F" },
        "1015": { name: "Light Ivory", HEX: "#E6D690" },
        "1016": { name: "Sulfur Yellow", HEX: "#EDFF21" },
        "1017": { name: "Saffron Yellow", HEX: "#F5D033" },
        "1018": { name: "Zinc Yellow", HEX: "#F8F32B" },
        "1019": { name: "Gray Beige", HEX: "#9E9764" },
        "1020": { name: "Olive Yellow", HEX: "#999950" },
        "1021": { name: "Rape Yellow", HEX: "#F3DA0B" },
        "1023": { name: "Traffic Yellow", HEX: "#FAD201" },
        "1024": { name: "Ochre Yellow", HEX: "#AEA04B" },
        "1026": { name: "Luminous Yellow", HEX: "#FFFF00" },
        "1027": { name: "Curry", HEX: "#9D9101" },
        "1028": { name: "Melon Yellow", HEX: "#F4A900" },
        "1032": { name: "Broom Yellow", HEX: "#D6AE01" },
        "1033": { name: "Dahlia Yellow", HEX: "#F3A505" },
        "1034": { name: "Pastel Yellow", HEX: "#EFA94A" },
        "1035": { name: "Tapenade", HEX: "#6A5D4D" },
        "1036": { name: "Roasted Coffee", HEX: "#705335" },
        "1037": { name: "Sun Yellow", HEX: "#F39F18" },
        "2000": { name: "Yellow Orange", HEX: "#ED760E" },
        "2001": { name: "Red Orange", HEX: "#C93C20" },
        "2002": { name: "Vermilion", HEX: "#CB2821" },
        "2003": { name: "Pastel Orange", HEX: "#FF7514" },
        "2004": { name: "Pure Orange", HEX: "#F44611" },
        "2005": { name: "Luminous Orange", HEX: "#FF2301" },
        "2007": { name: "Luminous Bright Orange", HEX: "#FFA420" },
        "2008": { name: "Bright Red Orange", HEX: "#F75E25" },
        "2009": { name: "Traffic Orange", HEX: "#F54021" },
        "2010": { name: "Signal Orange", HEX: "#D84B20" },
        "2011": { name: "Deep Orange", HEX: "#EC7C26" },
        "2012": { name: "Salmon Orange", HEX: "#E55137" },
        "2013": { name: "Pearl Orange(Metalic Color)", HEX: "#C35831" },
        "3000": { name: "Flame Red", HEX: "#AF2B1E" },
        "3001": { name: "Signal Red", HEX: "#A52019" },
        "3002": { name: "Carmine Red", HEX: "#A2231D" },
        "3003": { name: "Ruby Red", HEX: "#9B111E" },
        "3004": { name: "Purple Red", HEX: "#75151E" },
        "3005": { name: "Wine Red", HEX: "#5E2129" },
        "3007": { name: "Black Red", HEX: "#412227" },
        "3009": { name: "Oxide Red", HEX: "#642424" },
        "3011": { name: "Brown Red", HEX: "#781F19" },
        "3012": { name: "Beige Red", HEX: "#C1876B" },
        "3013": { name: "Tomato Red", HEX: "#A12312" },
        "3014": { name: "Antique Pink", HEX: "#D36E70" },
        "3015": { name: "Light Pink", HEX: "#EA899A" },
        "3016": { name: "Coral Red", HEX: "#B32821" },
        "3017": { name: "Rose", HEX: "#E63244" },
        "3018": { name: "Strawberry Red", HEX: "#D53032" },
        "3020": { name: "Traffic Red", HEX: "#CC0605" },
        "3022": { name: "Salmon Pink", HEX: "#D95030" },
        "3024": { name: "Luminous Red", HEX: "#F80000" },
        "3026": { name: "Luminous Bright Red", HEX: "#FE0000" },
        "3027": { name: "Raspberry Red", HEX: "#C51D34" },
        "3028": { name: "Pure Red", HEX: "#CB3234" },
        "3031": { name: "Orient Red", HEX: "#B32428" },
        "3032": { name: "Pearl Ruby Red(Metalic Color)", HEX: "#721422" },
        "3033": { name: "Pearl Pink(Metalic Color)", HEX: "#B44C43" },
        "4001": { name: "Red Lilac", HEX: "#6D3F5B" },
        "4002": { name: "Red Violet", HEX: "#922B3E" },
        "4003": { name: "Heather Violet", HEX: "#DE4C8A" },
        "4004": { name: "Claret Violet", HEX: "#641C34" },
        "4005": { name: "Blue Lilac", HEX: "#6C4675" },
        "4006": { name: "Traffic Purple", HEX: "#A03472" },
        "4007": { name: "Purple Violet", HEX: "#4A192C" },
        "4008": { name: "Signal Violet", HEX: "#924E7D" },
        "4009": { name: "Pastel Violet", HEX: "#A18594" },
        "4010": { name: "Telemagenta", HEX: "#CF3476" },
        "4011": { name: "Pearl Violet(Metalic Color)", HEX: "#8673A1" },
        "4012": { name: "Pearl Black Berry(Metalic Color)", HEX: "#6C6874" },
        "5000": { name: "Violet Blue", HEX: "#354D73" },
        "5001": { name: "Green Blue", HEX: "#1F3438" },
        "5002": { name: "Ultramarine Blue", HEX: "#20214F" },
        "5003": { name: "Saphire Blue", HEX: "#1D1E33" },
        "5004": { name: "Black Blue", HEX: "#18171C" },
        "5005": { name: "Signal Blue", HEX: "#1E2460" },
        "5007": { name: "Brillant Blue", HEX: "#3E5F8A" },
        "5008": { name: "Gray Blue", HEX: "#26252D" },
        "5009": { name: "Azure Blue", HEX: "#025669" },
        "5010": { name: "Gentian Blue", HEX: "#0E294B" },
        "5011": { name: "Steel Blue", HEX: "#231A24" },
        "5012": { name: "Light Blue", HEX: "#3B83BD" },
        "5013": { name: "Cobalt Blue", HEX: "#1E213D" },
        "5014": { name: "Pigeon Blue", HEX: "#606E8C" },
        "5015": { name: "Sky Blue", HEX: "#2271B3" },
        "5017": { name: "Traffic Blue", HEX: "#063971" },
        "5018": { name: "Turquoise Blue", HEX: "#3F888F" },
        "5019": { name: "Capri Blue", HEX: "#1B5583" },
        "5020": { name: "Ocean Blue", HEX: "#1D334A" },
        "5021": { name: "Water Blue", HEX: "#256D7B" },
        "5022": { name: "Night Blue", HEX: "#252850" },
        "5023": { name: "Distant Blue", HEX: "#49678D" },
        "5024": { name: "Pastel Blue", HEX: "#5D9B9B" },
        "5025": { name: "Pearl Gentian Blue(Metalic Color)", HEX: "#2A6478" },
        "5026": { name: "Pearl Night Blue(Metalic Color)", HEX: "#102C54" },
        "6000": { name: "Patina Green", HEX: "#316650" },
        "6001": { name: "Emerald Green", HEX: "#287233" },
        "6002": { name: "Leaf Green", HEX: "#2D572C" },
        "6003": { name: "Olive Green", HEX: "#424632" },
        "6004": { name: "Blue Green", HEX: "#1F3A3D" },
        "6005": { name: "Moss Green", HEX: "#2F4538" },
        "6006": { name: "Gray Olive", HEX: "#3E3B32" },
        "6007": { name: "Bottle Green", HEX: "#343B29" },
        "6008": { name: "Brown Green", HEX: "#39352A" },
        "6009": { name: "Fir Green", HEX: "#31372B" },
        "6010": { name: "Grass Green", HEX: "#35682D" },
        "6011": { name: "Reseda Green", HEX: "#587246" },
        "6012": { name: "Black Green", HEX: "#343E40" },
        "6013": { name: "Reed Green", HEX: "#6C7156" },
        "6014": { name: "Yellow Olive", HEX: "#47402E" },
        "6015": { name: "Black Olive", HEX: "#3B3C36" },
        "6016": { name: "Turquoise Green", HEX: "#1E5945" },
        "6017": { name: "May Green", HEX: "#4C9141" },
        "6018": { name: "Yellow Green", HEX: "#57A639" },
        "6019": { name: "Pastel Green", HEX: "#BDECB6" },
        "6020": { name: "Chrome Green", HEX: "#2E3A23" },
        "6021": { name: "Pale Green", HEX: "#89AC76" },
        "6022": { name: "Olive Grab", HEX: "#25221B" },
        "6024": { name: "Traffic Green", HEX: "#308446" },
        "6025": { name: "Fern Green", HEX: "#3D642D" },
        "6026": { name: "Opal Green", HEX: "#015D52" },
        "6027": { name: "Light Green", HEX: "#84C3BE" },
        "6028": { name: "Pine Green", HEX: "#2C5545" },
        "6029": { name: "Mint Green", HEX: "#20603D" },
        "6032": { name: "Signal Green", HEX: "#317F43" },
        "6033": { name: "Mint Turquoise", HEX: "#497E76" },
        "6034": { name: "Pastel Turquoise", HEX: "#7FB5B5" },
        "6035": { name: "Pearl Green(Metalic Color)", HEX: "#1C542D" },
        "6036": { name: "Pearl Opal Green(Metalic Color)", HEX: "#193737" },
        "6037": { name: "Pure Green", HEX: "#008F39" },
        "6038": { name: "Luminous Green", HEX: "#00BB2D" },
        "7000": { name: "Squirrel Gray", HEX: "#78858B" },
        "7001": { name: "Silver Gray", HEX: "#8A9597" },
        "7002": { name: "Olive Gray", HEX: "#7E7B52" },
        "7003": { name: "Moss Gray", HEX: "#6C7059" },
        "7004": { name: "Signal Gray", HEX: "#969992" },
        "7005": { name: "Mouse Gray", HEX: "#646B63" },
        "7006": { name: "Beige Gray", HEX: "#6D6552" },
        "7008": { name: "Khaki Gray", HEX: "#6A5F31" },
        "7009": { name: "Green Gray", HEX: "#4D5645" },
        "7010": { name: "Tarpaulin Gray", HEX: "#4C514A" },
        "7011": { name: "Iron Gray", HEX: "#434B4D" },
        "7012": { name: "Basalt Gray", HEX: "#4E5754" },
        "7013": { name: "Brown Gray", HEX: "#464531" },
        "7015": { name: "Slate Gray", HEX: "#434750" },
        "7016": { name: "Anthracite Gray", HEX: "#293133" },
        "7021": { name: "Black Gray", HEX: "#23282B" },
        "7022": { name: "Umbra Gray", HEX: "#332F2C" },
        "7023": { name: "Concrete Gray", HEX: "#686C5E" },
        "7024": { name: "Graphite Gray", HEX: "#474A51" },
        "7026": { name: "Granite Gray", HEX: "#2F353B" },
        "7030": { name: "Stone Gray", HEX: "#8B8C7A" },
        "7031": { name: "Blue Gray", HEX: "#474B4E" },
        "7032": { name: "Pebble Gray", HEX: "#B8B799" },
        "7033": { name: "Cement Gray", HEX: "#7D8471" },
        "7034": { name: "Yellow Gray", HEX: "#8F8B66" },
        "7035": { name: "Light Gray", HEX: "#D7D7D7" },
        "7036": { name: "Platinum Gray", HEX: "#7F7679" },
        "7037": { name: "Dusty Gray", HEX: "#7D7F7D" },
        "7038": { name: "Agate Gray", HEX: "#B5B8B1" },
        "7039": { name: "Quartz Gray", HEX: "#6C6960" },
        "7040": { name: "Window Gray", HEX: "#9DA1AA" },
        "7042": { name: "Traffic Gray A", HEX: "#8D948D" },
        "7043": { name: "Traffic Gray B", HEX: "#4E5452" },
        "7044": { name: "Silk Gray", HEX: "#CAC4B0" },
        "7045": { name: "TeleGray 1", HEX: "#909090" },
        "7046": { name: "TeleGray 2", HEX: "#82898F" },
        "7047": { name: "TeleGray 4", HEX: "#D0D0D0" },
        "7048": { name: "Pearl Mouse Gray(Metalic Color)", HEX: "#898176" },
        "8000": { name: "Green Brown", HEX: "#826C34" },
        "8001": { name: "Ochre Brown", HEX: "#955F20" },
        "8002": { name: "Signal Brown", HEX: "#6C3B2A" },
        "8003": { name: "Clay Brown", HEX: "#734222" },
        "8004": { name: "Copper Brown", HEX: "#8E402A" },
        "8007": { name: "Fawn Brown", HEX: "#59351F" },
        "8008": { name: "Olive Brown", HEX: "#6F4F28" },
        "8011": { name: "Nut Brown", HEX: "#5B3A29" },
        "8012": { name: "Red Brown", HEX: "#592321" },
        "8014": { name: "Sepia Brown", HEX: "#382C1E" },
        "8015": { name: "Chestnut Brown", HEX: "#633A34" },
        "8016": { name: "Mahogany Brown", HEX: "#4C2F27" },
        "8017": { name: "Chocolate Brown", HEX: "#45322E" },
        "8019": { name: "Gray Brown", HEX: "#403A3A" },
        "8022": { name: "Black Brown", HEX: "#212121" },
        "8023": { name: "Orange Brown", HEX: "#A65E2E" },
        "8024": { name: "Beige Brown", HEX: "#79553D" },
        "8025": { name: "Pale Brown", HEX: "#755C48" },
        "8028": { name: "Terra Brown", HEX: "#4E3B31" },
        "8029": { name: "Pearl Copper(Metalic Color)", HEX: "#763C28" },
        "9001": { name: "Cream", HEX: "#FDF4E3" },
        "9002": { name: "Gray White", HEX: "#E7EBDA" },
        "9003": { name: "Signal White", HEX: "#F4F4F4" },
        "9004": { name: "Signal Black", HEX: "#282828" },
        "9005": { name: "Jet Black", HEX: "#0A0A0A" },
        "9006": { name: "White Aluminium (Metalic Color)", HEX: "#A5A5A5" },
        "9007": { name: "Gray Aluminium(Metalic Color)", HEX: "#8F8F8F" },
        "9010": { name: "Pure White", HEX: "#FFFFFF" },
        "9011": { name: "Graphite Black", HEX: "#1C1C1C" },
        "9016": { name: "Traffic White", HEX: "#F6F6F6" },
        "9017": { name: "Traffic Black", HEX: "#1E1E1E" },
        "9018": { name: "Papyrus White", HEX: "#D7D7D7" },
        "9022": { name: "Pearl Light Gray(Metalic Color)", HEX: "#9C9C9C" },
        "9023": { name: "Pearl Dark Gray(Metalic Color)", HEX: "#828282" },
    } as const;

    private static readonly ANODIZED_COLORS: Record<string, { name: string; HEX: string }> = {
        "LV" : { name: "Light Silver", HEX: "#AFAFAF" },
        "RST" : { name: "Dark Silver", HEX: "#858585" },
        "ES200" : { name: "Light Grey", HEX: "#C9BFAB" },
        "ES400" : { name: "Grey", HEX: "#857049" },
        "ES600" : { name: "Dark Grey", HEX: "#554536" },
        "ES900" : { name: "Black", HEX: "#252525" },
        "G-10" : { name: "Light Gold", HEX: "#C8BEA9" },
        "G-11" : { name: "Gold", HEX: "#BAA972" },
        "G-12" : { name: "Dark Gold", HEX: "#BCA450" },
    } as const;

    /**
     * Käänteinen hakukartta HEX -> { RAL, name }
     */
    private static readonly HEX_TO_RAL: Record<string, { RAL: string; name: string }> = {};

    /**
     * Käänteinen hakukartta anodisoitu-HEX → (anodisoitu koodi, nimi)
     */
    private static readonly HEX_TO_ANODIZED: Record<string, { code: string; name: string }> = {};

    // Staattinen alustuslohkossa rakennetaan käänteiset mapit
    static {
        // 1) RAL
        for (const [ral, data] of Object.entries(ColorConverter.RAL_DATA)) {
            const hexUpper = data.HEX.toUpperCase();
            ColorConverter.HEX_TO_RAL[hexUpper] = { RAL: ral, name: data.name };
        }

        // 2) Anodisoidut värit
        for (const [code, data] of Object.entries(ColorConverter.ANODIZED_COLORS)) {
            const hexUpper = data.HEX;
            ColorConverter.HEX_TO_ANODIZED[hexUpper] = { code, name: data.name };
        }
    }

    /**
     * Syötä joko:
     * - RAL-koodi (esim. "1000" tai "RAL 1000"),
     * - Anodisoitu koodi (esim. "GS-02"), TAI
     * - HEX-arvo (esim. "#BEBD7F").
     *
     * Palauttaa { RAL, name, HEX }, tai `null` jos ei löydy listalta.
     *
     * @param input Värikoodi tai HEX.
     * @param palette Suodattaa värit "RAL", "Anodized", tai `null` kaikille väreille.
     */
    public static convert(input: string, palette: null | 'RAL' | 'Anodized' = null): ColorInfo | null {
        let trimmed = input.trim().toUpperCase();
        // Fixes new color codes with "à" character
        trimmed = trimmed.replace(/À/g, 'à');

        // Jos syöte on HEX-arvo
        if (trimmed.startsWith("#")) {
            // Etsitään määritellystä paletista tai kaikista paleteista
            if (palette === 'RAL' || palette === null) {
                const ralData = ColorConverter.HEX_TO_RAL[trimmed];
                if (ralData) {
                    return {
                        RAL: ralData.RAL,
                        name: ralData.name,
                        HEX: trimmed,
                    };
                }
            }
            if (palette === 'Anodized' || palette === null) {
                const anodizedData = ColorConverter.HEX_TO_ANODIZED[trimmed];
                if (anodizedData) {
                    return {
                        RAL: anodizedData.code,
                        name: anodizedData.name,
                        HEX: trimmed,
                    };
                }
            }
            console.error(`ColorConverter: Unknown HEX color ${input}`);
            return null;
        }

        // Jos syöte on koodi (RAL tai anodisoitu)
        if (palette === 'RAL' || palette === null) {
            const ralCode = trimmed.replace(/^RAL\s*/, ""); // Poistetaan mahdollinen "RAL "-prefiksi
            const ralData = ColorConverter.RAL_DATA[ralCode];
            if (ralData) {
                return {
                    RAL: ralCode,
                    name: ralData.name,
                    HEX: ralData.HEX.toUpperCase(),
                };
            }
        }
        if (palette === 'Anodized' || palette === null) {
            const anodizedData = ColorConverter.ANODIZED_COLORS[trimmed];
            if (anodizedData) {
                return {
                    RAL: trimmed, // Tässä "RAL" tarkoittaa anodisoitua koodia
                    name: anodizedData.name,
                    HEX: anodizedData.HEX.toUpperCase(),
                };
            }
        }

        // Palautetaan `null`, jos syöte ei löytynyt
        return null;
    }

    /**
     * Palauttaa kaikki RAL-värit taulukkona { RAL, name, HEX }.
     */
    public static getAllColors(palette : 'RAL' | 'Anodized' = 'RAL'): ColorInfo[] {
        switch (palette) {
            case 'RAL':
                return Object.entries(ColorConverter.RAL_DATA).map(([ral, data]) => ({
                    RAL: ral,
                    name: data.name,
                    HEX: data.HEX.toUpperCase(),
                }));
            case 'Anodized':
                return Object.entries(ColorConverter.ANODIZED_COLORS).map(([code, data]) => ({
                    RAL: code, // "RAL"-kenttään tallennetaan anodisoitu koodi
                    name: data.name,
                    HEX: data.HEX.toUpperCase(),
                }));
            default:
                throw new Error(`Unknown palette: ${palette}`);
        }

    }

    /**
     * Palauttaa värien lukumäärän.
     * @param hex
     */
    static getLuminance(hex: string): number {
        // Poistetaan mahdollinen # merkki
        hex = hex.replace(/^#/, '');

        // Muutetaan RGB-komponenteiksi
        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;

        // Sovelletaan W3C:n luminanssikaavaa
        const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
        const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
        const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

        return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
    }
}
